import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API, auth, inputCls, Field, Modal, PageHead, PrimaryBtn, FinanceEmpty, errMsg } from './finance/shared';
import Icon from '../../components/dashboard/DashboardIcons';

export const TICKET_STATUS_LABELS = {
  open: { text: 'مفتوحة', cls: 'bg-blue-50 text-blue-700' },
  in_progress: { text: 'قيد المعالجة', cls: 'bg-amber-50 text-amber-700' },
  waiting_customer: { text: 'بانتظار ردك', cls: 'bg-purple-50 text-purple-700' },
  resolved: { text: 'تم الحل', cls: 'bg-emerald-50 text-emerald-700' },
  closed: { text: 'مغلقة', cls: 'bg-slate-100 text-slate-500' },
};
export const TICKET_PRIORITY_LABELS = {
  low: { text: 'منخفضة', cls: 'bg-slate-100 text-slate-600' },
  medium: { text: 'متوسطة', cls: 'bg-blue-50 text-blue-700' },
  high: { text: 'عالية', cls: 'bg-orange-50 text-orange-700' },
  urgent: { text: 'عاجلة', cls: 'bg-red-50 text-red-600' },
};
export const TICKET_CATEGORY_LABELS = { technical: 'تقني', billing: 'مالي', feature_request: 'طلب ميزة', general: 'عام', other: 'أخرى' };

const fmtDateTime = (iso) => { if (!iso) return ''; try { return new Date(iso).toLocaleString('ar-SA'); } catch { return iso; } };

const StatCard = ({ label, value, active, onClick, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`rounded-xl border p-3 text-right transition-colors ${active ? 'border-[#16233b] bg-[#16233b] text-white' : 'border-slate-200 bg-white text-slate-800 hover:border-slate-300'}`}
  >
    <div className={`text-xs ${active ? 'text-slate-300' : 'text-slate-500'}`}>{label}</div>
    <div className="mt-1 text-xl font-bold">{value ?? '—'}</div>
  </button>
);

export default function SupportPage() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({ subject: '', description: '', category: 'general', priority: 'medium' });
  const [formErr, setFormErr] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      axios.get(`${API}/support-tickets/summary`, auth()),
      axios.get(`${API}/support-tickets`, { ...auth(), params: statusFilter ? { status: statusFilter } : {} }),
    ]).then(([sRes, tRes]) => {
      setSummary(sRes.data || {});
      setTickets(Array.isArray(tRes.data) ? tRes.data : []);
    }).catch((e) => setError(errMsg(e, 'تعذّر تحميل مذكرات الدعم'))).finally(() => setLoading(false));
  }, [statusFilter]);

  useEffect(load, [load]);

  const openNew = () => {
    setForm({ subject: '', description: '', category: 'general', priority: 'medium' });
    setFormErr(null);
    setModalOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.subject.trim().length < 3 || form.description.trim().length < 5) {
      setFormErr('العنوان والوصف مطلوبان (وصف لا يقل عن 5 أحرف)');
      return;
    }
    setSubmitting(true);
    setFormErr(null);
    try {
      const res = await axios.post(`${API}/support-tickets`, form, auth());
      setModalOpen(false);
      navigate(`/support/${res.data.id}`);
    } catch (err) {
      setFormErr(errMsg(err, 'فشل إنشاء المذكرة'));
    } finally {
      setSubmitting(false);
    }
  };

  const chips = [
    ['', 'الكل', summary?.total],
    ['open', 'مفتوحة', summary?.open],
    ['in_progress', 'قيد المعالجة', summary?.in_progress],
    ['waiting_customer', 'بانتظار ردك', summary?.waiting_customer],
    ['resolved', 'تم الحل', summary?.resolved],
    ['closed', 'مغلقة', summary?.closed],
  ];

  return (
    <div className="mx-auto max-w-5xl space-y-4" dir="rtl" data-testid="support-page">
      <PageHead
        title="الدعم"
        desc="مذكراتك السابقة مع فريق الدعم، وإنشاء مذكرة جديدة عند الحاجة"
        action={<PrimaryBtn onClick={openNew} data-testid="support-new-ticket-btn"><Icon name="plus" className="w-4 h-4" /> مذكرة جديدة</PrimaryBtn>}
      />

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6" data-testid="support-summary-cards">
        {chips.map(([key, label, value]) => (
          <StatCard key={key} label={label} value={value} active={statusFilter === key} onClick={() => setStatusFilter(key)} testId={`support-summary-${key || 'all'}`} />
        ))}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white">
        <div className="border-b border-slate-100 px-4 py-3 text-sm font-bold text-slate-800">مذكراتي ({tickets.length})</div>
        {loading ? (
          <div className="space-y-2 p-4">{[1, 2, 3].map((i) => <div key={i} className="h-14 animate-pulse rounded-lg bg-slate-100" />)}</div>
        ) : error ? (
          <div className="p-6 text-center text-sm text-red-600" data-testid="support-list-error">{error}</div>
        ) : tickets.length === 0 ? (
          <FinanceEmpty text="لا توجد مذكرات دعم بعد" />
        ) : (
          <div className="divide-y divide-slate-50">
            {tickets.map((t) => (
              <button
                key={t.id}
                onClick={() => navigate(`/support/${t.id}`)}
                data-testid="support-ticket-row"
                className="flex w-full flex-wrap items-center justify-between gap-2 px-4 py-3 text-right hover:bg-slate-50"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-mono text-slate-400" dir="ltr">{t.ticket_number}</span>
                    <span className="truncate text-sm font-bold text-slate-800">{t.subject}</span>
                  </div>
                  <div className="mt-0.5 text-xs text-slate-400">{TICKET_CATEGORY_LABELS[t.category] || t.category} · آخر تحديث {fmtDateTime(t.updated_at)}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${TICKET_PRIORITY_LABELS[t.priority]?.cls}`}>{TICKET_PRIORITY_LABELS[t.priority]?.text}</span>
                  <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${TICKET_STATUS_LABELS[t.status]?.cls}`} data-testid="support-ticket-status-badge">{TICKET_STATUS_LABELS[t.status]?.text}</span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      <Modal open={modalOpen} title="مذكرة دعم جديدة" onClose={() => setModalOpen(false)}>
        {formErr && <div className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="support-form-error">{formErr}</div>}
        <form onSubmit={submit} className="space-y-3">
          <Field label="عنوان المذكرة" required>
            <input className={inputCls} value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="support-form-subject" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="التصنيف">
              <select className={inputCls} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="support-form-category">
                {Object.entries(TICKET_CATEGORY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </Field>
            <Field label="الأولوية">
              <select className={inputCls} value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} data-testid="support-form-priority">
                {Object.entries(TICKET_PRIORITY_LABELS).map(([k, v]) => <option key={k} value={k}>{v.text}</option>)}
              </select>
            </Field>
          </div>
          <Field label="وصف المشكلة أو الطلب" required>
            <textarea rows={4} className={inputCls} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="support-form-description" />
          </Field>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setModalOpen(false)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">إلغاء</button>
            <PrimaryBtn type="submit" disabled={submitting} data-testid="support-form-submit">{submitting ? 'جارٍ الإرسال...' : 'إرسال المذكرة'}</PrimaryBtn>
          </div>
        </form>
      </Modal>
    </div>
  );
}
